/**
 * Offline Backup Service
 * LocalStorage-based backup for document edits during network disruptions
 */

import { logger } from '../utils/logger';

interface EditSession {
  content: string;
  lastModified: string;
  documentNumber?: string;
  documentId?: string;
  projectId?: string;
  attempts: number;
}

const STORAGE_PREFIX = 'offline-edit-';
const MAX_ATTEMPTS = 5;

export const offlineBackup = {
  /**
   * Save an edit session to localStorage
   */
  saveEditSession(
    documentKey: string,
    content: string,
    metadata: {
      documentNumber?: string;
      documentId?: string;
      projectId?: string;
    }
  ): void {
    try {
      const session: EditSession = {
        content,
        lastModified: new Date().toISOString(),
        documentNumber: metadata.documentNumber,
        documentId: metadata.documentId,
        projectId: metadata.projectId,
        attempts: 0,
      };
      
      localStorage.setItem(`${STORAGE_PREFIX}${documentKey}`, JSON.stringify(session));
    } catch (error) {
      logger.error('Failed to save to localStorage:', error);
      // LocalStorage full or unavailable - user will lose changes if browser crashes
    }
  },

  /**
   * Retrieve an edit session from localStorage
   */
  getEditSession(documentKey: string): EditSession | null {
    try {
      const data = localStorage.getItem(`${STORAGE_PREFIX}${documentKey}`);
      if (!data) return null;
      
      return JSON.parse(data) as EditSession;
    } catch (error) {
      logger.error('Failed to retrieve from localStorage:', error);
      return null;
    }
  },

  /**
   * Clear an edit session after successful save
   */
  clearEditSession(documentKey: string): void {
    try {
      localStorage.removeItem(`${STORAGE_PREFIX}${documentKey}`);
    } catch (error) {
      logger.error('Failed to clear localStorage:', error);
    }
  },

  /**
   * Get all pending edit sessions
   */
  getAllPendingSessions(): Array<{ key: string; session: EditSession }> {
    const sessions: Array<{ key: string; session: EditSession }> = [];
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX)) {
          const session = this.getEditSession(key.replace(STORAGE_PREFIX, ''));
          if (session) {
            sessions.push({ key: key.replace(STORAGE_PREFIX, ''), session });
          }
        }
      }
    } catch (error) {
      logger.error('Failed to list pending sessions:', error);
    }
    
    return sessions;
  },

  /**
   * Increment retry attempt counter
   */
  incrementAttempts(documentKey: string): void {
    const session = this.getEditSession(documentKey);
    if (session) {
      session.attempts += 1;
      
      if (session.attempts >= MAX_ATTEMPTS) {
        // Too many failures - keep the backup but mark as failed
        logger.warn(`Max retry attempts reached for ${documentKey}`);
      }
      
      localStorage.setItem(`${STORAGE_PREFIX}${documentKey}`, JSON.stringify(session));
    }
  },
};
